import React, { useEffect, useState } from "react";

const DEFAULT_QUALITY = 60;

const isRemoteImage = (src = "") => /^https?:\/\//i.test(src);

const canUseVercelImageOptimization = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (process.env.REACT_APP_USE_VERCEL_IMAGE_OPTIMIZATION === "true") {
    return true;
  }

  return /(^|\.)vercel\.app$/i.test(window.location.hostname);
};

export const buildVercelImageUrl = (src, width, quality = DEFAULT_QUALITY) =>
  `/_vercel/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;

export const getOptimizedImageUrl = (src, width, quality = DEFAULT_QUALITY) => {
  if (!isRemoteImage(src) || !canUseVercelImageOptimization()) {
    return src;
  }

  return buildVercelImageUrl(src, width, quality);
};

export const getOptimizedSrcSet = (
  src,
  widths = [],
  quality = DEFAULT_QUALITY,
) => {
  if (
    !isRemoteImage(src) ||
    !canUseVercelImageOptimization() ||
    !Array.isArray(widths) ||
    widths.length < 2
  ) {
    return undefined;
  }

  return widths
    .map((candidateWidth) => {
      const candidateSrc = buildVercelImageUrl(src, candidateWidth, quality);
      return `${candidateSrc} ${candidateWidth}w`;
    })
    .join(", ");
};

const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  widths,
  sizes,
  quality = DEFAULT_QUALITY,
  ...props
}) => {
  const [hasOptimizationError, setHasOptimizationError] = useState(false);

  useEffect(() => {
    setHasOptimizationError(false);
  }, [src, width, height, quality]);

  const responsiveWidths =
    Array.isArray(widths) && widths.length ? widths : width ? [width] : [];
  const largestWidth =
    responsiveWidths.length > 0
      ? Math.max(...responsiveWidths)
      : width || undefined;

  const resolvedSrc = hasOptimizationError
    ? src
    : getOptimizedImageUrl(src, largestWidth, quality);
  const srcSet = hasOptimizationError
    ? undefined
    : getOptimizedSrcSet(src, responsiveWidths, quality);

  if (!src) {
    return null;
  }

  const handleImageError = (event) => {
    if (!hasOptimizationError && resolvedSrc !== src) {
      setHasOptimizationError(true);
      return;
    }

    if (typeof props.onError === "function") {
      props.onError(event);
    }
  };

  return (
    <img
      {...props}
      src={resolvedSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={srcSet ? sizes : undefined}
      srcSet={srcSet}
      onError={handleImageError}
    />
  );
};

export default OptimizedImage;
